// Create this as: src/components/partial/PaymentProgressModal.jsx

import React from 'react';
import { Modal, Button } from 'react-bootstrap';

const PaymentProgressModal = ({
  show,
  onClose,
  paymentState,
  paymentError,
  invoice,
  installer,
  onRetry,
  onCancel
}) => {
  
  const getProgressContent = () => {
    switch (paymentState) {
      case "initializing":
        return {
          icon: "⚡",
          title: "Initializing Payment",
          message: "Setting up payment processing...",
          showSpinner: true,
          showCancel: true
        };

      case "creating_intent":
        return {
          icon: "💳",
          title: "Preparing Transaction",
          message: `Creating payment for $${(invoice.amount_due / 100).toFixed(2)}`,
          showSpinner: true,
          showCancel: true
        };

      case "checking_terminal":
        return {
          icon: "📡",
          title: "Connecting to Card Reader",
          message: "Checking terminal status...",
          showSpinner: true,
          showCancel: true
        };

      case "terminal_offline":
        return {
          icon: "📴",
          title: "Card Reader Offline",
          message: "Please check that the card reader is powered on and connected",
          showSpinner: false,
          error: true,
          showRetry: true,
          showCancel: true
        };

      case "sending_to_reader":
        return {
          icon: "📤",
          title: "Sending to Card Reader",
          message: "Preparing reader for payment...",
          showSpinner: true,
          showCancel: true
        };

      case "waiting_for_customer":
        return {
          icon: "👆",
          title: "Ready for Payment",
          message: "Customer can now insert, tap, or swipe their card",
          showSpinner: false,
          highlight: true,
          subMessage: "Waiting for card...",
          showCancel: true
        };
        
      case "processing":
        return {
          icon: "⏳",
          title: "Processing Payment",
          message: "Please wait while payment is processed...",
          showSpinner: true
        };
        
      case "success":
        return {
          icon: "✅",
          title: "Payment Successful!",
          message: "Transaction completed successfully",
          showSpinner: false,
          success: true
        };
        
      case "declined":
        return {
          icon: "❌",
          title: "Payment Declined",
          message: "The card was declined. Please try a different payment method.",
          showSpinner: false,
          error: true,
          showRetry: true
        };
        
      case "error":
        return {
          icon: "⚠️",
          title: "Payment Error",
          message: paymentError || "An error occurred processing the payment",
          showSpinner: false,
          error: true,
          showRetry: true
        };
        
      default:
        return {
          icon: "💳",
          title: "Payment Processing",
          message: "Initializing...",
          showSpinner: true
        };
    }
  };
  
  const content = getProgressContent();
  
  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header>
        <Modal.Title className="w-100 text-center">
          <div className="logosize">
            <img 
              src={require('../../assets/Jobvious_logo.png')} 
              alt="Jobvious" 
              style={{height: '40px'}} 
            />
          </div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="text-center py-5">
        <div className={`payment-progress ${content.highlight ? 'highlight' : ''}`}>
          
          {/* Progress Icon */}
          <div className="progress-icon mb-4" style={{fontSize: '4rem'}}>
            {content.icon}
          </div>
          
          {/* Title */}
          <h3 className={`mb-3 ${content.success ? 'text-success' : ''} ${content.error ? 'text-danger' : ''}`}>
            {content.title}
          </h3>
          
          {/* Main Message */}
          <p className="lead mb-3">{content.message}</p>
          
          {/* Sub Message */}
          {content.subMessage && (
            <p className="text-muted mb-4">{content.subMessage}</p>
          )}
          
          {/* Spinner */}
          {content.showSpinner && (
            <div className="spinner-border text-primary mb-4" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          )}
          
          {/* Highlighted Info Box */}
          {content.highlight && (
            <div className="alert alert-info border-0" style={{backgroundColor: '#e3f2fd'}}>
              <div className="row">
                <div className="col-6">
                  <strong>Amount:</strong><br />
                  <span className="h4 text-success">${(invoice.amount_due / 100).toFixed(2)}</span>
                </div>
                <div className="col-6">
                  <strong>Customer:</strong><br />
                  <span>{invoice.customer_name || 'Customer'}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Pulsing animation for waiting states */}
          {content.highlight && (
            <div className="mt-3">
              <div 
                className="pulse-dot mx-auto"
                style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#28a745',
                  borderRadius: '50%',
                  animation: 'pulse 2s infinite'
                }}
              ></div>
            </div>
          )}
          
          {/* Cancel Button for non-error states */}
          {content.showCancel && !content.error && onCancel && (
            <div className="mt-4">
              <Button
                variant="outline-secondary"
                onClick={onCancel}
              >
                CANCEL PAYMENT
              </Button>
            </div>
          )}

          {/* Error/Retry Actions */}
          {content.error && (
            <div className="mt-4">
              {content.showRetry && (
                <Button
                  variant="primary"
                  className="me-3"
                  onClick={onRetry}
                >
                  Try Again
                </Button>
              )}
              <Button
                variant="outline-secondary"
                onClick={onCancel || onClose}
              >
                Cancel
              </Button>
            </div>
          )}
          
          {/* Success Auto-close message */}
          {content.success && (
            <div className="mt-4">
              <small className="text-muted">Closing automatically...</small>
            </div>
          )}
          
        </div>
      </Modal.Body>
      
      {/* Only show close button for non-processing states */}
      {!content.showSpinner && !content.success && (
        <Modal.Footer className="justify-content-center">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default PaymentProgressModal;